import React from 'react';
import { byMethodName } from '../../../../data/followup/constants/payment_methods';
import { createMoney, formatCurrency } from '../../../../data/shared/money';
import { ChatBubbleLeftEllipsisIcon, UserCircleIcon, InformationCircleIcon } from '@heroicons/react/20/solid'
import i18n from '../../../../i18n';
import {MessageContent} from "./Action";

const EntryContainer = ({children, isLast})=> {
    return <li>
        <div className="relative pb-8">
            {!isLast ? (
                <span className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
            ) : null}
            <div className="relative flex items-start space-x-3">
                {children}
            </div>
        </div>
    </li>
}
const Avatar = ({author, chat = false}) => <>
    {author.avatar ?
        <div className="relative">
            <img
                className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-400 ring-8 ring-white"
                src={author.avatar}
                alt=""
            />
            {chat && <span className="absolute -bottom-0.5 -right-1 rounded-tl bg-white px-0.5 py-px">
                <ChatBubbleLeftEllipsisIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </span>}
        </div> :
        <div>
            <div className="relative px-1">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 ring-8 ring-white">
                    {author.name ? <UserCircleIcon className="h-5 w-5 text-gray-500" aria-hidden="true" /> :
                        <InformationCircleIcon className="h-5 w-5 text-gray-500" aria-hidden="true" />}
                </div>
            </div>
        </div>
    }
</>

const Date = ({date}) => {
    return <p className="mt-0.5 text-sm text-gray-500">{date}</p>
}

const QuotationCreation = ({ meta: { date, author }, ...others }) =>
    <EntryContainer {...others}>
        <Avatar author={author} />
        <div className="min-w-0 flex-1 py-1.5">
            <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-900">
                    {author.name}
                </span>{' '}
                {i18n.t('followup.entry_create_quotation')}
                <Date date={date.format('LLL')} />
            </div>
        </div>
    </EntryContainer>
;

const QuotationRequest = ({ meta: { date, author }, ...others }) =>
    <EntryContainer {...others}>
        <Avatar author={author} />
        <div className="min-w-0 flex-1 py-1.5">
            <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-900">
                    {author.name}
                </span>{' '}
                {i18n.t('followup.entry_create_reservation')}
                <Date date={date.format('LLL')} />
            </div>
        </div>
    </EntryContainer>
;

const CustomerMessage = ({ meta: { date, author }, data: { message }, ...others }) =>
    <EntryContainer {...others}>
        <Avatar author={author} chat={true} />
        <div className="min-w-0 flex-1 py-1.5">
            <div>
                <div className="text-sm">
                    <span className="font-medium text-gray-900">
                        {i18n.t('followup.entry_message_from')} {author.name}
                    </span>
                </div>
                <p className="mt-0.5 text-sm text-gray-500">{date.format('LLL')}</p>
            </div>
            <div className="mt-2 text-sm text-gray-700">
                <MessageContent>{message}</MessageContent>
            </div>
        </div>
    </EntryContainer>
;

const UserMessage = ({ meta: { date, author }, data: { message }, ...others }) =>
    <EntryContainer {...others}>
        <Avatar author={author} chat={true} />
        <div className="min-w-0 flex-1 py-1.5">
            <div>
                <div className="text-sm">
                    <span className="font-medium text-gray-900">
                        {i18n.t('followup.entry_message_from')} {author.name}
                    </span>
                </div>
                <p className="mt-0.5 text-sm text-gray-500">{date.format('LLL')}</p>
            </div>
            <div className="mt-2 text-sm text-gray-700">
                <MessageContent>{message}</MessageContent>
            </div>
        </div>
    </EntryContainer>
;


const QuotationSubmit = ({ meta: { date, author }, data: { numbering }, ...others }) =>
    <EntryContainer {...others}>
        <Avatar author={author} />
        <div className="min-w-0 flex-1 py-1.5">
            <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-900">
                    {author.name}
                </span>{' '}
                {i18n.t('followup.entry_proposed_quotation')} { numbering }
                <Date date={date.format('LLL')} />
            </div>
        </div>
    </EntryContainer>
;

const Wip = ({ meta: { date, author, ...others } }) =>
    <EntryContainer {...others}>
        <Avatar author={author} />
        <div className="min-w-0 flex-1 py-1.5">
            <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-900">
                    {author.name}
                </span>{' '}
                {i18n.t('followup.entry_is_working_on_demand')}
                <Date date={date.format('LLL')} />
            </div>
        </div>
    </EntryContainer>
;

const QuotationReject = ({ meta: { date, author }, data, ...others }) =>
    <EntryContainer {...others}>
        <Avatar author={author} />
        <div className="min-w-0 flex-1 py-1.5">
            <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-900">
                    {author.name}
                </span>{' '}
                {i18n.t('followup.entry_refused_demand')}
                {data && data.reason && <span> - raison : <em>"{data.reason}"</em></span>}

                <Date date={date.format('LLL')} />
            </div>
        </div>
    </EntryContainer>
;

const QuotationCancellation = ({ meta: { date, author }, data: { numbering }, ...others }) =>
    <EntryContainer {...others}>
        <Avatar author={author} />
        <div className="min-w-0 flex-1 py-1.5">
            <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-900">
                    {author.name}
                </span>{' '}
                {i18n.t('followup.entry_cancel_quotation')} {numbering}
                <Date date={date.format('LLL')} />
            </div>
        </div>
    </EntryContainer>
;

const QuotationUpdated = ({ meta: { date, author }, ...others }) =>
    <EntryContainer {...others}>
        <Avatar author={author} />
        <div className="min-w-0 flex-1 py-1.5">
            <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-900">
                    {author.name}
                </span>{' '}
                {i18n.t('followup.entry_update_request')}
                <Date date={date.format('LLL')} />
            </div>
        </div>
    </EntryContainer>

const QuotationUpdateCancelled = ({ meta: { date, author }, ...others }) =>
    <EntryContainer {...others}>
        <Avatar author={author} />
        <div className="min-w-0 flex-1 py-1.5">
            <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-900">
                    {author.name}
                </span>{' '}
                {i18n.t('followup.entry_update_cancellation_request')}
                <Date date={date.format('LLL')} />
            </div>
        </div>
    </EntryContainer>
;

const QuotationArchiving = ({ meta: { date, author }, ...others }) =>
    <EntryContainer {...others}>
        <Avatar author={author} />
        <div className="min-w-0 flex-1 py-1.5">
            <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-900">
                    {author.name}
                </span>{' '}
                {i18n.t('followup.entry_archive_demand')}
                <Date date={date.format('LLL')} />
            </div>
        </div>
    </EntryContainer>
;

const QuotationAcceptation = ({ meta: { date, author }, data: { numbering }, ...others  }) =>
    <EntryContainer {...others}>
        <Avatar author={author} />
        <div className="min-w-0 flex-1 py-1.5">
            <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-900">
                    {author.name}
                </span>{' '}
                {i18n.t('followup.entry_accepted_quotation')} {numbering}
                <Date date={date.format('LLL')} />
            </div>
        </div>
    </EntryContainer>
;

const ReservationCreation = ({ isFirst, meta: { date, author }, ...others }) =>
    <EntryContainer {...others}>
        <Avatar author={author} />
        <div className="min-w-0 flex-1 py-1.5">
            <div className="text-sm text-gray-500">
                {author.name && <span className="font-medium text-gray-900">
                    {author.name}
                </span>}{' '}
                {i18n.t(author.name ? 'followup.entry_created_reservation'  : 'followup.entry_reservation_created')}
                <Date date={date.format('LLL')} />
            </div>
        </div>
    </EntryContainer>


;

const ReservationChange = ({ meta: { date, author }, ...others }) =>
    <EntryContainer {...others}>
        <Avatar author={author} />
        <div className="min-w-0 flex-1 py-1.5">
            <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-900">
                    {author.name}
                </span>{' '}
                {i18n.t('followup.entry_update_reservation')}
                <Date date={date.format('LLL')} />
            </div>
        </div>
    </EntryContainer>
;

const ReservationAddPayment = ({ meta: { date, author }, data: { value, method }, ...others }) =>
    <EntryContainer {...others}>
        <Avatar author={author} />
        <div className="min-w-0 flex-1 py-1.5">
            <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-900">
                    {author.name}
                </span>{' '}
                &nbsp;{i18n.t('followup.entry_record_payment')} {formatCurrency(createMoney(value))} {byMethodName(method)}
                <Date date={date.format('LLL')} />
            </div>
        </div>
    </EntryContainer>
;

const ReservationAddRefund = ({ meta: { date, author }, data: { value, method }, ...others }) =>
    <EntryContainer {...others}>
        <Avatar author={author} />
        <div className="min-w-0 flex-1 py-1.5">
            <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-900">
                    {author.name}
                </span>{' '}
                &nbsp;{i18n.t('followup.entry_record_refund')} {formatCurrency(createMoney(value))} {byMethodName(method)}
                <Date date={date.format('LLL')} />
            </div>
        </div>
    </EntryContainer>
;

const ReservationTerminate = ({ meta: { date, author }, ...others }) =>
    <EntryContainer {...others}>
        <Avatar author={author} />
        <div className="min-w-0 flex-1 py-1.5">
            <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-900">
                    {author.name}
                </span>{' '}
                {i18n.t('followup.entry_reservation_ended')}
                <Date date={date.format('LLL')} />
            </div>
        </div>
    </EntryContainer>
;

const ReservationCancel = ({ meta: { date, author }, data, ...others }) =>
    <EntryContainer {...others}>
        <Avatar author={author} />
        <div className="min-w-0 flex-1 py-1.5">
            <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-900">
                    {author.name}
                </span>{' '}
                {i18n.t('followup.entry_cancel_reservation')}
                <Date date={date.format('LLL')} />
                {data && data.reason && <span> - raison : <em>"{data.reason}"</em></span>}
            </div>
        </div>
    </EntryContainer>
;

export {
    QuotationCreation,
    QuotationRequest,
    CustomerMessage,
    Wip,
    UserMessage,
    QuotationSubmit,
    QuotationCancellation,
    QuotationReject,
    QuotationArchiving,
    QuotationAcceptation,
    QuotationUpdated,
    QuotationUpdateCancelled,
    ReservationCreation,
    ReservationChange,
    ReservationAddPayment,
    ReservationAddRefund,
    ReservationTerminate,
    ReservationCancel
};
